import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import SavedViewsBar from "@/components/SavedViewsBar";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import BadLeadsFilters from "@/components/badleads/BadLeadsFilters";
import BadLeadsTable from "@/components/badleads/BadLeadsTable";

/**
 * Bad-lead reports queue. Fetches the list + enums, and hands rendering off
 * to the sub-components in `components/badleads/`.
 */
export default function BadLeadsQueue() {
  const { hasPerm } = useAuth();
  const [params, setParams] = useSearchParams();
  const [status, setStatus] = useState(params.get("status") || "");
  const [reason, setReason] = useState(params.get("reason") || "");
  const [q, setQ] = useState(params.get("q") || "");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [enums, setEnums] = useState({ statuses: [], reasons: [] });
  const [selected, setSelected] = useState({});
  const canReview = hasPerm("review_bad_leads");

  useEffect(() => {
    api.get("/bad-leads/_meta/enums").then((r) => setEnums(r.data));
  }, []);

  const load = async () => {
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (status) p.set("status", status);
    if (reason) p.set("reason", reason);
    p.set("limit", "200");
    const { data } = await api.get(`/bad-leads?${p}`);
    setItems(data.reports);
    setTotal(data.total);
    setParams(p);
    setSelected({});
  };

  // Refetch whenever the (dropdown-based) status or reason filter changes.
  useEffect(() => { load(); }, [status, reason]);

  const applySaved = (f) => {
    setStatus(f.status || "");
    setReason(f.reason || "");
    setQ(f.q || "");
    toast.success("View applied");
  };

  const bulkClose = async () => {
    const selectedIds = Object.keys(selected).filter((k) => selected[k]);
    if (!selectedIds.length) return toast.error("Select at least one report");
    const notes = prompt("Closing note (visible in status history):", "Bulk close");
    if (notes === null) return;
    try {
      const { data } = await api.post("/bulk/bad-leads/close", {
        report_ids: selectedIds,
        notes,
      });
      toast.success(`Closed ${data.closed}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  // Stable reference so SavedViewsBar doesn't re-render every parent render.
  const currentFilters = useMemo(() => ({ q, status, reason }), [q, status, reason]);

  return (
    <div>
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="label-mono">Quality</div>
          <h1 className="font-chivo font-black text-4xl md:text-5xl uppercase tracking-tighter leading-none">
            Bad leads / returns
          </h1>
        </div>
      </div>

      <SavedViewsBar viewType="bad_leads" currentFilters={currentFilters} onApply={applySaved} />

      <BadLeadsFilters
        q={q} setQ={setQ}
        status={status} setStatus={setStatus}
        reason={reason} setReason={setReason}
        enums={enums}
        onApply={load}
      />

      <BadLeadsTable
        items={items}
        total={total}
        selected={selected}
        setSelected={setSelected}
        canReview={canReview}
        onBulkClose={bulkClose}
      />
    </div>
  );
}
